import { HStack } from "@chakra-ui/react";
import React, { useCallback, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { AiInterviewerFragment } from "../../graphql";
import { useFeatureFlagForCurrentOrImpersonatedUser } from "../../graphql/hooks/useFeatureFlag";
import { useIsScreenOrgAtsConnected } from "../../hooks/useIsScreenOrgAtsConnected";
import AiInterviewerCompletionNotifications from "./AiInterviewerCompletionNotifications";
import AiInterviewerNotificationsCompletionSettings from "./AiInterviewerNotificationsCompletionSettings";
import AiInterviewerNotificationsInvitationSettings from "./AiInterviewerNotificationsInvitationSettings";
import AiInterviewerNotificationsInvitationSmsSettings from "./AiInterviewerNotificationsInvitationSmsSettings";
import {
  AiInterviewerNotificationsNav,
  NOTIFICATION_VIEW_BY_SLUG,
  NOTIFICATION_VIEW_SLUGS,
  NotificationView,
} from "./AiInterviewerNotificationsNav";
import { AiInterviewerNotificationsReminderSettings } from "./AiInterviewerNotificationsReminderSettings";
import AiInterviewerNotificationsSettings from "./AiInterviewerNotificationsSettings";
import AiInterviewerSection from "./AiInterviewerSection";
import { useAiInterviewerFormContext } from "./useAiInterviewerFormContext";

type AiInterviewerNotificationsProps = {
  aiInterviewer: AiInterviewerFragment;
  readOnly: boolean;
};

const AiInterviewerNotifications: React.FC<AiInterviewerNotificationsProps> = ({
  aiInterviewer,
  readOnly,
}) => {
  const isAtsLess = !useIsScreenOrgAtsConnected();
  const smsFeatureFlagEnabled =
    useFeatureFlagForCurrentOrImpersonatedUser("ai_interviewer:sms");
  // SMS notifications are only available when the org has a dedicated AI
  // Interview phone number to send from.
  const aiInterviewSmsEnabled =
    smsFeatureFlagEnabled && !!aiInterviewer.aiInterviewPhoneNumber;
  const { watch } = useAiInterviewerFormContext();

  const { aiInterviewerId } = useParams<"aiInterviewerId">();
  const location = useLocation();
  const navigate = useNavigate();

  const defaultView: NotificationView = isAtsLess
    ? "completion"
    : "notificationSettings";

  const notificationsSegment = location.pathname.split("/notifications/")[1];
  const urlSlug = notificationsSegment?.split("/")[0];
  const viewFromUrl = urlSlug ? NOTIFICATION_VIEW_BY_SLUG[urlSlug] : undefined;
  const activeView: NotificationView = viewFromUrl ?? defaultView;

  const setActiveView = useCallback(
    (view: NotificationView): void => {
      if (!aiInterviewerId) return;
      navigate(
        {
          pathname: `/ai-interviewer/${aiInterviewerId}/notifications/${NOTIFICATION_VIEW_SLUGS[view]}`,
          search: location.search,
        },
        { replace: true }
      );
    },
    [aiInterviewerId, navigate, location.search]
  );

  useEffect(() => {
    if (isAtsLess && activeView !== "completion") {
      setActiveView("completion");
    } else if (!viewFromUrl) {
      setActiveView(defaultView);
    }
  }, [isAtsLess, activeView, viewFromUrl, defaultView, setActiveView]);

  // Watch form values that drive the nav badges
  const reminderEmailEnabled = watch("reminderEmailEnabled") ?? false;
  const completionEmailEnabled = watch("completionEmailEnabled") ?? false;
  const invitationSmsEnabled = watch("invitationSmsEnabled") ?? false;

  const renderContent = (): React.ReactNode => {
    switch (activeView) {
      case "notificationSettings":
        return (
          <AiInterviewerNotificationsSettings
            readOnly={readOnly}
            aiInterviewSmsEnabled={aiInterviewSmsEnabled}
          />
        );
      case "invitationEmail":
        return (
          <AiInterviewerNotificationsInvitationSettings
            aiInterviewer={aiInterviewer}
            readOnly={readOnly}
          />
        );
      case "invitationSms":
        return (
          <AiInterviewerNotificationsInvitationSmsSettings
            aiInterviewer={aiInterviewer}
            readOnly={readOnly}
          />
        );
      case "reminder":
        return (
          <AiInterviewerNotificationsReminderSettings
            aiInterviewer={aiInterviewer}
            readOnly={readOnly}
          />
        );
      case "completionEmail":
        return (
          <AiInterviewerNotificationsCompletionSettings
            aiInterviewer={aiInterviewer}
            readOnly={readOnly}
          />
        );
      case "completion":
        return <AiInterviewerCompletionNotifications readOnly={readOnly} />;
      default:
        return null;
    }
  };

  return (
    <HStack w="100%" spacing={4} alignItems="flex-start" py="4">
      <AiInterviewerNotificationsNav
        activeView={activeView}
        onActiveViewChange={setActiveView}
        isAtsLess={isAtsLess}
        aiInterviewSmsEnabled={aiInterviewSmsEnabled}
        invitationEmailEnabled
        invitationSmsEnabled={invitationSmsEnabled}
        reminderEmailEnabled={reminderEmailEnabled}
        completionEnabled={completionEmailEnabled}
      />
      <AiInterviewerSection flex="1">{renderContent()}</AiInterviewerSection>
    </HStack>
  );
};

export default AiInterviewerNotifications;
